import DOMPurify from "dompurify";
import { ComponentPropsWithoutRef, ElementType } from "react";

// Only Tiptap's privacy-enhanced YouTube embeds may be framed (same host as CSP frame-src and the backend sanitizer).
const YOUTUBE_EMBED_PREFIX = "https://www.youtube-nocookie.com/embed/";

DOMPurify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName === "iframe" && !(node as Element).getAttribute("src")?.startsWith(YOUTUBE_EMBED_PREFIX)) {
    node.parentNode?.removeChild(node);
  }
});

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (
    node.tagName === "A" &&
    node.getAttribute("target")?.toLowerCase() === "_blank"
  ) {
    node.setAttribute("rel", "noopener noreferrer nofollow");
  }
});

// Mirrors the backend RichTextSanitizer style allowlist; existing stored HTML still carries arbitrary styles.
const ALLOWED_STYLES: Record<string, RegExp> = {
  color: /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|[a-z]+)$/i,
  "text-align": /^(left|center|right|justify)$/i,
};

const sanitizeStyle = (style: string): string =>
  style
    .split(";")
    .flatMap((declaration) => {
      const separator = declaration.indexOf(":");
      if (separator === -1) return [];
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      // hasOwn guard: inherited keys like "constructor" must not resolve to Object.prototype members.
      return Object.hasOwn(ALLOWED_STYLES, property) && ALLOWED_STYLES[property].test(value) ? [`${property}: ${value}`] : [];
    })
    .join("; ");

DOMPurify.addHook("uponSanitizeAttribute", (_node, data) => {
  if (data.attrName === "style") {
    data.attrValue = sanitizeStyle(data.attrValue);
    data.keepAttr = data.attrValue !== "";
  }
});

type SafeHtmlProps<T extends ElementType> = {
  html: string | null | undefined;
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, "dangerouslySetInnerHTML" | "children">;

export const SafeHtml = <T extends ElementType = "div">({
  html,
  as,
  ...props
}: SafeHtmlProps<T>) => {
  const Tag = (as ?? "div") as ElementType;

  return (
    <Tag
      {...props}
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(html ?? "", { ADD_TAGS: ["iframe"], ADD_ATTR: ["target", "allowfullscreen"] }),
      }}
    />
  );
};

export default SafeHtml;
